/**
 * Pont entre l'API Gamepad du navigateur et le fournisseur d'entree C++.
 *
 * Le navigateur n'emet pas d'evenement quand un bouton change : il faut
 * scruter navigator.getGamepads() a chaque image. On ne pousse vers le WASM
 * que les valeurs qui ont reellement bouge, pour eviter une vingtaine
 * d'appels par image.
 *
 * Les index de boutons et d'axes suivent la disposition standard, celle-la
 * meme qu'utilise le profil « xbox » de Play! sur ordinateur.
 */

import * as Play from './PlayModule';

/*
 * PlayModule est un `export let` initialise a null, puis reassigne quand le
 * module WASM est pret. Un `import { PlayModule }` fige la valeur observee au
 * moment de la liaison dans certains contextes de bundling : on passe donc par
 * l'espace de noms, dont la lecture est toujours differee jusqu'a l'appel.
 */
function playModule(): any {
	return Play.PlayModule;
}

/** En deca, on considere le stick au repos : les manettes derivent toujours un peu. */
const DEADZONE = 0.12;

let running = false;
let frame = 0;
const lastButtons: number[] = [];
const lastAxes: number[] = [];

export interface GamepadInfo {
	connected: boolean;
	id: string;
}

let currentInfo: GamepadInfo = { connected: false, id: '' };
type Listener = (info: GamepadInfo) => void;
const listeners = new Set<Listener>();

export function onGamepadChange(listener: Listener): () => void {
	listeners.add(listener);
	listener(currentInfo);
	return () => listeners.delete(listener);
}

function publish(info: GamepadInfo) {
	if (info.connected === currentInfo.connected && info.id === currentInfo.id) return;
	currentInfo = info;
	listeners.forEach((l) => l(currentInfo));
}

/** Premiere manette reellement connectee. */
function pickGamepad(): Gamepad | null {
	const pads = navigator.getGamepads ? navigator.getGamepads() : [];
	for (const pad of pads) {
		if (pad && pad.connected) return pad;
	}
	return null;
}

function poll() {
	if (!running) return;
	frame = requestAnimationFrame(poll);

	const pad = pickGamepad();
	if (!pad) {
		publish({ connected: false, id: '' });
		return;
	}
	publish({ connected: true, id: pad.id });

	const mod = playModule();
	if (!mod?.gamepadButton) return;

	for (let i = 0; i < pad.buttons.length; i++) {
		// Les gachettes analogiques (L2/R2 sur Xbox) exposent une valeur
		// continue ; `pressed` suffit ici, le jeu ne lit que du tout ou rien.
		const pressed = pad.buttons[i].pressed ? 1 : 0;
		if (lastButtons[i] !== pressed) {
			lastButtons[i] = pressed;
			try {
				mod.gamepadButton(i, pressed);
			} catch {
				/* module pas pret */
			}
		}
	}

	for (let i = 0; i < pad.axes.length; i++) {
		let value = pad.axes[i];
		if (Math.abs(value) < DEADZONE) value = 0;
		// On arrondit au centieme : sans cela le bruit du stick declenche un
		// appel a chaque image, meme immobile.
		const rounded = Math.round(value * 100) / 100;
		if (lastAxes[i] !== rounded) {
			lastAxes[i] = rounded;
			try {
				mod.gamepadAxis(i, rounded);
			} catch {
				/* module pas pret */
			}
		}
	}
}

export function startGamepadPolling() {
	if (running) return;
	running = true;
	frame = requestAnimationFrame(poll);
}

export function stopGamepadPolling() {
	running = false;
	if (frame) cancelAnimationFrame(frame);
	frame = 0;
	lastButtons.length = 0;
	lastAxes.length = 0;
}
